<template>
  <ConfirmDialog
    :open="open"
    max-height="530"
    :disabled-confirm-action="loading"
    @cancel="onCancel"
    @confirm="onConfirm"
  >
    <template #title>
      {{ enableTfa ? 'Set up 2-Factor Authentication' : 'Disable 2-Factor Authentication' }}
    </template>
    <template #body>
      <div class="form__content">
        <template v-if="loading">
          <Spinner
            size="md"
            color="dark"
            label="2FA setup"
          />
        </template>
        <template v-else>
          <SetupTfaForm
            v-if="showSetupForm"
            class="form__tfa-form"
            :email="email"
            :secret-key="secretKey"
          />
          <LoginTfaForm
            v-else
            v-model:token="token"
            class="form__tfa-form"
            :show-error="showConfirmError"
            @confirm="confirm2fa"
          />
        </template>
      </div>
    </template>
  </ConfirmDialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import LoginTfaForm from '@/modules/Auth/LoginTfaForm.vue'
import SetupTfaForm from '@/modules/Auth/SetupTfaForm.vue'
import { useAuthStore } from '@/modules/Auth/useAuthStore'
import { useToast } from '@/uiKit/Toast/useToast'
import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'
import Spinner from '@/uiKit/Spinner/Spinner.vue'
import { useUserStore } from '@/modules/Auth/useUserStore'

const props = withDefaults(
  defineProps<{
    open: boolean
    enableTfa: boolean
    setupRequired?: boolean
  }>(),
  {
    setupRequired: true,
  },
)

const authStore = useAuthStore()
const toast = useToast()

const secretKey = ref<string | null>(null)
const showSetupForm = ref<boolean>(true)
const showConfirmError = ref<boolean>(false)
const token = ref<string>()

const userStore = useUserStore()
const email = computed(() => userStore.currentUser?.email || '')
const loading = ref<boolean>(false)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'confirmed'): void
}>()

const setup2fa = async (): Promise<void> => {
  const result = await authStore.setup2fa()

  if ('error' in result) {
    toast.warning({
      meta: { title: result.error.message.toString() },
    })
    return
  }

  secretKey.value = result.data.secret_2fa
}

const confirm2fa = async (): Promise<void> => {
  if (!token.value) return

  const result = await authStore.confirm2fa({
    token: token.value,
  })

  if ('error' in result) {
    toast.warning({
      meta: { title: result.error.message.toString() },
    })
    showConfirmError.value = true
    token.value = ''

    return
  }

  emit('confirmed')
  emit('close')
}

const onCancel = (): void => {
  if (showSetupForm.value || !props.setupRequired) {
    emit('close')
  } else {
    showSetupForm.value = true
    showConfirmError.value = false
  }
}

const onConfirm = (): void => {
  if (showSetupForm.value) {
    showSetupForm.value = false
    showConfirmError.value = false
  } else {
    confirm2fa()
  }
}

watch(
  () => props.setupRequired,
  () => {
    showSetupForm.value = props.setupRequired
  },
  { immediate: true },
)

watch(
  () => props.open,
  async (opened) => {
    if (opened) {
      loading.value = true
      secretKey.value = null
      token.value = ''

      if (props.setupRequired) {
        await setup2fa()
      }

      loading.value = false
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.form__content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
  min-height: 200px;
}

.form__tfa-form {
  width: 100%;
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
/*  This `DarwinDialog` modal is normally called from `<modal>` and it needs to be on top of it.
    There is no current support to control z-index, so the below line is a workaround.
    `.setup-tfa-dialog` is added to the dialog so we make sure the leaked css isn't
    changing the `z-index` of other dialogs.
*/
.darwin-dialog.setup-tfa-dialog {
  z-index: var(--z-index-modal-level-2-overlay) !important;
}
</style>
